import React, { useState, useEffect } from 'react';
import styles from './SalaryModal.module.css';
import { FaPlus, FaMoneyBillWave, FaTimes, FaTrash, FaEdit } from 'react-icons/fa';
import AddSalaryModal from './AddSalaryModal';
import { useTheme } from '../../../contexts/ThemeContext';
import { collection, query, where, getDocs, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../../../config/firebaseConfig';
import { toast } from 'react-toastify';

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', { 
        style: 'currency', 
        currency: 'TRY' 
    }).format(amount).replace('₺', '') + ' ₺';
};

const SalaryModal = ({ salaries: initialSalaries, setSalaries: setParentSalaries, selectedEmployee, onClose }) => {
    const { isDarkMode } = useTheme();
    const [showAddSalaryModal, setShowAddSalaryModal] = useState(false);
    const [salaries, setSalaries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingSalary, setEditingSalary] = useState(null);

    useEffect(() => {
        if (selectedEmployee && selectedEmployee.id) {
            fetchEmployeeSalaries();
        }
    }, [selectedEmployee]);

    const fetchEmployeeSalaries = async () => {
        if (!selectedEmployee || !selectedEmployee.id) return;

        try {
            setLoading(true);
            const salaryQuery = query(
                collection(db, 'Salaries'),
                where('employeeId', '==', selectedEmployee.id)
            );
            const querySnapshot = await getDocs(salaryQuery);

            const salaryData = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            // Sort by timestamp (createdAt or updatedAt) in descending order (most recent first)
            salaryData.sort((a, b) => {
                const timeA = a.updatedAt || a.createdAt || 0;
                const timeB = b.updatedAt || b.createdAt || 0;
                return timeB - timeA; // Descending order
            });

            setSalaries(salaryData);
            setParentSalaries(salaryData);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching salary data:', error);
            toast.error('Maaş bilgileri alınırken bir hata oluştu');
            setLoading(false);
        }
    };

    const handleAddSalaryClick = () => {
        setEditingSalary(null);
        setShowAddSalaryModal(true);
    };

    const handleEditSalary = (salary) => {
        setEditingSalary(salary);
        setShowAddSalaryModal(true);
    };

    const handleSalaryAdded = (newSalary) => {
        const updatedSalaries = [...salaries, newSalary];
        setSalaries(updatedSalaries);
        setParentSalaries(updatedSalaries);
    };

    const handleSalaryUpdated = (updatedSalary) => {
        const updatedSalaries = salaries.map(salary =>
            salary.id === updatedSalary.id ? updatedSalary : salary
        );
        setSalaries(updatedSalaries);
        setParentSalaries(updatedSalaries);
    };

    const handleDeleteSalary = async (salaryId) => {
        if (!window.confirm('Bu maaş kaydını silmek istediğinize emin misiniz?')) {
            return;
        }

        try {
            await deleteDoc(doc(db, 'Salaries', salaryId));
            const updatedSalaries = salaries.filter(salary => salary.id !== salaryId);
            setSalaries(updatedSalaries);
            setParentSalaries(updatedSalaries);
            toast.success('Maaş kaydı başarıyla silindi');
        } catch (error) {
            console.error('Error deleting salary:', error);
            toast.error('Maaş kaydı silinirken bir hata oluştu');
        }
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div className={styles.modalOverlay} onClick={handleOverlayClick}>
            <div className={styles.modalContent}>
                <div className={styles.modalHeader}>
                    <h2 className={styles.modalTitle}>
                        <FaMoneyBillWave className={styles.modalIcon} />
                        Maaş Bilgileri
                    </h2>
                    <button
                        onClick={handleAddSalaryClick}
                        className={styles.addButton}
                    >
                        <FaPlus /> Yeni Maaş Ekle
                    </button>
                    <button 
                        className={styles.closeButton} 
                        onClick={onClose}
                        aria-label="Kapat"
                    >
                        <FaTimes />
                    </button>
                </div>
                <div className={styles.modalBody}>
                    <div className={styles.tableContainer}>
                        <table className={styles.salaryTable}>
                            <thead>
                                <tr>
                                    <th>Sözleşme Tarihi</th>
                                    <th>Brüt Ücret</th>
                                    <th>SGK İşçi Payı (%14)</th>
                                    <th>İşsizlik Sig. İşçi Payı (%1)</th>
                                    <th>Damga Vergisi</th>
                                    <th>Kesintiler Toplamı</th>
                                    <th>Net Ücret</th>
                                    <th>SGK İşveren Payı (%15.5)</th>
                                    <th>İşsizlik Sig. İşveren Payı (%2)</th>
                                    <th>Toplam Maliyet</th>
                                    <th>İşlemler</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="11" className={styles.emptyMessage}>
                                            Maaş bilgileri yükleniyor...
                                        </td>
                                    </tr>
                                ) : salaries && salaries.length > 0 ? (
                                    salaries.map((salary, index) => {
                                        const grossSalary = Number(salary.grossSalary);
                                        const sskEmployeeShare = grossSalary * 0.14;
                                        const unemploymentEmployeeShare = grossSalary * 0.01;
                                        const stampTax = grossSalary * 0.00759;
                                        const totalDeductions = sskEmployeeShare + unemploymentEmployeeShare + stampTax;
                                        const netSalary = grossSalary - totalDeductions;
                                        const sskEmployerShare = grossSalary * 0.155;
                                        const unemploymentEmployerShare = grossSalary * 0.02;
                                        const totalEmployerCost = grossSalary + sskEmployerShare + unemploymentEmployerShare;

                                        return (
                                            <tr key={index}>
                                                <td>{salary.contractDate}</td>
                                                <td className={styles.positive}>{formatCurrency(grossSalary)}</td>
                                                <td className={styles.negative}>{formatCurrency(sskEmployeeShare)}</td>
                                                <td className={styles.negative}>{formatCurrency(unemploymentEmployeeShare)}</td>
                                                <td className={styles.negative}>{formatCurrency(stampTax)}</td>
                                                <td className={styles.negative}>{formatCurrency(totalDeductions)}</td>
                                                <td className={styles.positive}>{formatCurrency(netSalary)}</td>
                                                <td>{formatCurrency(sskEmployerShare)}</td>
                                                <td>{formatCurrency(unemploymentEmployerShare)}</td>
                                                <td>{formatCurrency(totalEmployerCost)}</td>
                                                <td>
                                                    <div className={styles.actionButtons}>
                                                        <button
                                                            className={styles.editButton}
                                                            onClick={() => handleEditSalary(salary)}
                                                            title="Maaş kaydını düzenle"
                                                        >
                                                            <FaEdit />
                                                        </button>
                                                        <button
                                                            className={styles.deleteButton}
                                                            onClick={() => handleDeleteSalary(salary.id)}
                                                            title="Maaş kaydını sil"
                                                        >
                                                            <FaTrash />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="11" className={styles.emptyMessage}>
                                            Bu çalışana ait herhangi bir maaş kaydı bulunmamaktadır.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className={styles.formActions}>
                    <button 
                        type="button" 
                        className={`${styles.actionButton} ${styles.dangerButton}`} 
                        onClick={onClose}
                    >
                        Kapat
                    </button>
                </div>
            </div>
            
            {showAddSalaryModal && selectedEmployee && (
                <AddSalaryModal
                    onClose={() => {
                        setShowAddSalaryModal(false);
                        setEditingSalary(null);
                    }}
                    onSalaryAdded={handleSalaryAdded}
                    onSalaryUpdated={handleSalaryUpdated}
                    employeeId={selectedEmployee.id}
                    editingSalary={editingSalary}
                />
            )}
        </div>
    );
};

export default SalaryModal;